<template>
  <main class="FilterPage">
    <AisInstantSearch :configuration="algoliaConfiguration" :widgets="algoliaWidgets">
      <div
        class="FilterPage__header"
        :class="{
          'FilterPage__header--imageless':
            !filterPageResult.filterPage.leadImage,
          'FilterPage__header--linkless':
            !filterPageResult.filterPage.filterLinks.length,
          'FilterPage__header--theme-promo': hasPromoTheme
        }"
      >
        <div
          v-if="filterPageResult.filterPage.leadImage"
          class="FilterPage__lead-image"
        >
          <DatoCmsImage
            :data="filterPageResult.filterPage.leadImage.responsiveImage"
            layout="fill"
            object-fit="cover"
            :object-position="`${
              filterPageResult.filterPage.leadImage.focalPoint.x * 100
            }% ${filterPageResult.filterPage.leadImage.focalPoint.y * 100}%`"
            :priority="true"
            sizes="(max-width: 600px) 600px, (max-width: 1000px) 1000px, 1400px"
          />
        </div>
        <div class="FilterPage__header-content-wrapper">
          <div class="FilterPage__header-content">
            <div>
              <!-- eslint-disable vue/no-v-html -->
              <h1
                class="FilterPage__title"
                v-html="formattedTitle"
              />
              <!-- eslint-enable vue/no-v-html -->
              <span v-if="!filterPageResult.filterPage.hidePageResultNumber" class="FilterPage__title-hits">
                <AisStats>
                  <template #default="{ nbHits, nbPages, page }">
                    <FilterHits
                      :nb-hits="nbHits"
                      :nb-pages="nbPages"
                      :current-page="page"
                    />
                  </template>
                </AisStats>
              </span>
            </div>
            <DatoStructuredText
              v-if="!isEmptyDocument(filterPageResult.filterPage.summary)"
              :content="filterPageResult.filterPage.summary"
              class="FilterPage__summary"
            />
          </div>
        </div>
        <ul
          v-if="filterPageResult.filterPage.filterLinks.length"
          class="FilterPage__filter-links"
        >
          <li
            v-for="filterLink in filterPageResult.filterPage.filterLinks"
            :key="filterLink.slug"
            class="FilterPage__filter-link"
          >
            <VisionButton
              :line-link="true"
              background-colour="white"
              display="inline-block"
              :spa-link="true"
              :to="
                '/' +
                  route.params.locale +
                  '/' +
                  filterLink.slug.replace(/__/g, '/').replace(/^\/+/, '')
              "
              size="xx-small"
              :button-text="filterLink.title"
              @click="handleClick"
            />
          </li>
        </ul>
      </div>

      <div
        v-if="campaignBanner && campaignBanner.type === 'styled_banner'"
        class="FilterPage__campaign-banner-wrapper"
      >
        <StyledBanner
          :background-colour="campaignBanner.backgroundColour"
          :condensed="campaignBanner.condensedView"
          :banner-style="campaignBanner.style"
          :image="campaignBanner.image"
          :mobile-image="campaignBanner.mobileImage"
          :content="campaignBanner.content"
          :text-colour="campaignBanner.textColour"
        />
      </div>

      <div>
        <div class="FilterPage__filter-facets">
          <div class="FilterPage__facet-container">
            <FilterFacets
              :facets-to-display="facetsToDisplay"
              class="FilterPage__facets"
            />
            <FilterSort
              :items="[
                {
                  value: algoliaIndex,
                  displayValue: 'Default',
                  selected: true,
                },
                {
                  value: `${algoliaIndex}_price_asc`,
                  displayValue: 'Price: Low to High',
                },
                {
                  value: `${algoliaIndex}_price_desc`,
                  displayValue: 'Price: High to Low',
                },
              ]"
              class="FilterPage__sort"
            />
          </div>
        </div>

        <div class="FilterPage__selected-filters">
          <FiltersSelected />
        </div>
      </div>

      <div
        v-if="campaignBanner && campaignBanner.type === 'filter_page_banner'"
        class="FilterPage__campaign-banner-wrapper FilterPage__campaign-banner-wrapper--below-filters"
      >
        <FilterPageBanner :banner="campaignBanner" />
      </div>

      <div class="FilterPage__results">
        <FilterResults
          :banners="banners"
          :positioning="filterPageResult.filterPage.galleryAdPositioning"
        />
      </div>
    </AisInstantSearch>

    <div
      v-if="filterPageResult.filterPage.showInstashop"
      class="FilterPage__instashop-wrapper"
    >
      <h3 class="FilterPage__instashop-title"> Get Inspired </h3>
      <InstagramGrid
        :posts="12"
        :products="
          (filterPageResult.filterPage.instashopProducts || []).map(
            (p) => p.id
          )
        "
      />
    </div>

    <div class="FilterPage__long-copy-container">
      <DatoStructuredText
        v-if="!isEmptyDocument(filterPageResult.filterPage.longFormatCopy)"
        :content="filterPageResult.filterPage.longFormatCopy"
        class="FilterPage__long-copy"
      />
    </div>
  </main>
</template>

<script setup>
/** DATO */
import { Image as DatoCmsImage } from 'vue-datocms'
import { isEmptyDocument } from 'datocms-structured-text-utils'

/** ALGOLIA */
import algoliasearch from "algoliasearch";
import { createFetchRequester } from "@algolia/requester-fetch";

/** STOREFRONT */
import filterPageQuery from '~/gql/filterPage.gql.js'
import {
  buildFacetsToDisplay,
  buildAlgoliaFilterString,
  buildBannersToDisplay,
 filterValidLensTechDisplay } from '~/assets/js/helpers/search/search'
import {
  parseText
} from '~/assets/js/helpers/text'


const { $sentry, $head, $track } = useNuxtApp()
const route = useRouter().currentRoute.value
const promoStore = usePromoStore()
const { localeForDato, getCorrectSlugForLocale } = useDatoLocale()
const {
  public: { algolia: { index: algoliaIndex, applicationId, apiKey } },
} = useRuntimeConfig()

const client = algoliasearch(applicationId, apiKey, {
  requester: createFetchRequester(),
});

const algoliaConfiguration = ref({
  indexName: algoliaIndex,
  searchClient: client,
  insights: true
});

const previewSlug = route.params?.filterpage?.join('__')

const slug =
  previewSlug ??
  getCorrectSlugForLocale(
    route.meta?.slugLocaleMapping,
    route.params?.filterpage?.join('__') || route.meta?.slug
  )

const id = route?.meta?.id

const variables = {
  locale: localeForDato,
  slug
}

if (id) {
  variables.id = id
}

const { data: filterPageResult, error: filterPageError } =
  await useDatoQuery({
    query: filterPageQuery,
    variables
  })

if (filterPageError.value) {
  console.error(filterPageError.value)

  $sentry.captureException(filterPageError.value)
}

const algoliaFilterString = buildAlgoliaFilterString(
  filterPageResult.value.filterPage.defaultFilters,
  filterPageResult.value.filterPage.enableCustomMerchandising === "yes"
    ? slug
    : undefined
)

const allPortraitBanners = computed(() => {
  return bannersForResults.value.inResultsPortraitBanners
})

const banners = computed(() => {
  return [...new Array(3)].map((_, idx) => ({
    type: 'banner',
    objectID: `banner-position${idx + 1}`,
    bannerIndex: idx,
    banners: allPortraitBanners.value.filter(
      (banner) => Number(banner.listPosition) === idx + 1
    )
  })).filter(banner => banner.banners.length)
})

const facetsToDisplay = computed(() => buildFacetsToDisplay(
  filterPageResult.value.filterPage.defaultFilters,
  filterPageResult.value.allFilterGroups
))

const bannersToDisplay = buildBannersToDisplay(
  filterPageResult.value.allFilterBanners,
  filterPageResult.value.filterPage.id,
  filterPageResult.value.filterPage.inheritGlobalGalleryAds
)

const bannersForResults = computed(() => {
  return {
    inResultsPortraitBanners:
      bannersToDisplay.inResultsPortraitBanners,
    inResultsLandscapeBanners:
      bannersToDisplay.inResultsLandscapeBanners,
  }
})

const campaignBanner = computed(() => {
  return filterPageResult.value.filterPage.campaignBanner
})

const formattedTitle = computed(() => {
  return parseText(filterPageResult.value.filterPage.title)
})

const hasPromoTheme = computed(() => {
  return promoStore.theme.id && (filterPageResult.value?.filterPage?.theme?.id === promoStore.theme.id)
})

const algoliaWidgets = computed(() => [
  useAisConfigure({
    searchParameters: {
      clickAnalytics: true,
      filters: algoliaFilterString,
      hitsPerPage: filterPageResult.value.filterPage.resultsPerPage ?? 36
    }
  }),
  useAisStats({}),
  useAisInfiniteHits({
    escapeHTML: true,
    transformItems: transformItemsForResults
  }),
  useAisClearRefinements({}),
  useAisCurrentRefinements({
    transformItems: transformRefinementsForDisplay
  }),
  useAisSortBy({
    items: [
      {
        value: algoliaIndex,
        displayValue: 'Default',
        selected: true,
      },
      {
        value: `${algoliaIndex}_price_asc`,
        displayValue: 'Price: Low to High',
      },
      {
        value: `${algoliaIndex}_price_desc`,
        displayValue: 'Price: High to Low',
      },
    ]
  }),
  ...Object.values(facetsToDisplay.value).map(facetToDisplay => {
    return useAisRefinementList({
      attribute: facetToDisplay.attributeForFacet,
      limit: 30,
      transformItems: transformFilterForDisplay
    })
  })
]);

function transformFilterForDisplay(items) {
  /**
   *
   * item.label.split('||')
   *
   * This is done this way as the Instantsearch RefinementList component
   * returns a label and a value from it's own internal implementation.
   *
   * `label` is typically the displayValue and `value` the "backend" held
   * value used to filter on.
   *
   * We have to store the swatch information in one of these two values
   * on the record in Algolia and then split it out on the frontend because
   * of the RefinementList data structure. The result of this split is piped
   * into our own `additionalFilterData` key to allow our FilterItem component
   * to consume it as needed for display.
   *
   */
  return items.map(item => {
    return {
      ...item,
      label: item.label.split('||')[0] ?? item.label,
      additionalFilterData: JSON.parse(item.label.split('||')[1] ?? null)
    }
  })
}

function transformRefinementsForDisplay(items) {
  return items.map((item) => {
    return {
      ...item,
      refinements: item.refinements.map((refinement) => {
        return {
          ...refinement,
          label: refinement.label.split('||')[0] ?? item.label,
          additionalFilterData: JSON.parse(
            refinement.label.split('||')[1] ?? null
          ),
          isRefined: true,
        }
      }),
    }
  })
}

function transformItemsForResults(items, { results: { page } }) {
  const { resolveLocalePath } = useUrlBuilder()

  const bannersToInject = page === 0 ? banners.value : []

  return bannersToInject.concat(items.map((item) => {
    return {
      objectID: item.objectID, // required for Algolia Insights (all events)
      queryId: item.__queryID, // required for Algolia Insights (click events)
      sku: item.sku,
      name: item.name,
      productInfoTitle: item.productInfoTitle,
      inStock: item.inStock,
      onPreorder: item.onPreorder,
      url: resolveLocalePath(item.url),
      legacySKU: item.legacySKU,
      prices: item.prices,
      logo: item.logo,
      lensTech: filterValidLensTechDisplay(item.lensTech),
      lensColour: item.lensColour?.displayValue,
      frameColour: item.frameColour?.displayValue || item.frameColour,
      isLimitedEdition: item.isLimitedEdition || false,
      variants: item.variants || [],
      reviews: item.reviews,
      tag: item.tag,
      promoTag: item.promoTag,
      respectLensTech: true,
      index: item.__position,
    }
  }))
}

async function handleClick() {
  await $track.global.linkClicked({
    interactionElement: 'Filter link',
    interactionSection: 'Filter results',
  })
}

useHead($head(filterPageResult.value.filterPage.meta))
</script>

<style lang="postcss">
.FilterPage__header {
  @apply gap-x-16 relative lg:py-28;

  @screen lgMax {
    @apply min-h-[20rem] flex flex-col justify-end;
  }
}

.FilterPage__header--imageless.FilterPage__header--linkless {
  @apply pt-10;

  .FilterPage__header-content {
    @apply pt-0;
  }
}

.FilterPage__header--imageless.FilterPage__header--linkless,
.FilterPage__header--linkless {
  @apply pb-10 lg:pb-16;

  .FilterPage__header-content {
    @apply pb-0;
  }
}

.FilterPage__header--imageless {
  @apply pt-10 pb-1;

  @screen lgMax {
    @apply min-h-0;
  }

  .FilterPage__header-content {
    @apply text-black;

    @screen lgMax {
      @apply pt-0 pb-1;
    }
  }

  .FilterPage__summary * {
    @apply font-normal;
  }

  .FilterPage__title-hits {
    @apply text-off-black;
  }

  & + .FilterPage__filter-links {
    @apply mt-2 mb-20;

    @screen lgMax {
      @apply mb-10;
    }
  }
}

.FilterPage__header-overlay {
  @apply relative;
}

.FilterPage__header-content-wrapper {
  @apply global-wrapper;
}

.FilterPage__header-content {
  @apply relative pt-5 pb-2 text-white;
}

.FilterPage__title {
  @apply inline heading-2 md:heading-1;
}

.FilterPage__title-hits {
  @apply text-white font-bold ml-2 align-text-bottom text-xxs md:text-base;
}

.FilterPage__title-hits {
  @apply inline-block;
}

.FilterPage__summary {
  @apply mt-2 sm:mt-4 max-w-2xl lg:max-w-xl xl:max-w-[740px];

  * {
    @apply font-bold;
  }

  p {
    @apply smMax:text-xxs;
  }
}

.FilterPage__lead-image {
  @apply absolute overflow-hidden top-0 left-0 w-full h-full;

  &::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(
      11deg,
      rgba(0, 0, 0, 0.5088629201680672) 0%,
      rgba(255, 255, 255, 0) 77%
    );

    @screen lg {
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.6209077380952381) 0%,
        rgba(255, 255, 255, 0) 77%
      );
    }
  }
}

.FilterPage__filter-links {
  @apply flex lgMax:overflow-x-auto scrollbar-none lg:flex-wrap lgMax:gap-3 lg:gap-4 mb-10 lg:mb-10 lg:mt-6 global-wrapper relative;
}

.FilterPage__filter-link {
  @apply lg:col-span-2;
}

.FilterPage__results {
  @apply pl-3 pr-3 lg:global-wrapper mt-8 lg:mt-10 min-h-[50vh];
}

.FilterPage__filter-facets {
  @apply border-y border-grey-400 lg:relative;
}

.FilterPage__facets {
  @apply w-1/2;
}

.FilterPage__facet-container {
  @apply lg:global-wrapper flex flex-row lg:gap-y-7 lg:justify-between lg:items-center;
}

.FilterPage__selected-filters {
  @apply global-wrapper overflow-x-auto;
}

.FilterPage__long-copy-container {
  @apply global-wrapper;
}

.FilterPage__long-copy {
  @apply my-24 sm:my-28;
}

.FilterPage__long-copy {
  @apply max-w-[740px];
}

.FilterPage__sort {
  @apply w-1/2 flex lg:w-auto;
}

.FilterPage__campaign-banner-wrapper {
  @apply mt-2 mb-6 md:mt-4 md:mb-8 lg:mt-4 lg:mb-10 global-wrapper;

  &--below-filters {
    @apply px-3 lg:global-wrapper;
  }
}

.FilterPage__instashop-wrapper {
  @apply py-3;
}

.FilterPage__instashop-title {
  @apply heading-2 global-wrapper
    mb-4 mt-md
    text-center md:text-left;
}

.FilterPage__header--theme-promo {
  .FilterPage__title {
    @apply promo-t-gradient promo-t-gradient-animated;
  }

  .FilterPage__title-hits {
    @apply text-[var(--promo-shine)];
  }

  &.FilterPage__header--imageless {
    .FilterPage__title {
      @apply promo-t-gradient-dark;
    }

    .FilterPage__title-hits {
      @apply text-[var(--promo-mid-bg)];
    }
  }
}
</style>
